import type { TTableBodyState } from '../../../../types/data_table';

import { CircleExclamation } from '../../../../assets/Icons';
import { DefaultSpreadsheetSolidFile } from '../../../../assets/Icons/files_types';

import { PageStatus } from '../../../PageStatus';
import { SkeletonBase } from '../../../SkeletonLoader/SkeletonBase';

// The three states are elements of the `table-body` BEM block, so they are
// styled from the block's own stylesheet rather than one of their own.
import styles from '../table-body.module.scss';

/** How many placeholder rows the skeleton draws while data is in flight. */
const SKELETON_ROWS = 10;

/**
 * Copy for the error state. It is baked in rather than taken from props
 * because a failed request is exactly the situation where the consumer may
 * have nothing useful to say.
 */
const ERROR_STATE_LOCALES = {
  en: {
    text: 'Something went wrong!',
    hintText: 'An error occurred while retrieving data, Please try again',
  },
  ar: {
    text: 'حدث خطأ ما!',
    hintText: 'حدث خطأ أثناء استرجاع البيانات، يرجى المحاولة مرة أخرى.',
  },
};

/**
 * What a `TableBody` shows when it has no rows to show: a loading skeleton, an
 * empty state, or an error state.
 */
export const TableBodyState = (props: TTableBodyState) => {
  const { status, lang = 'en' } = props;

  if (status === 'loading') {
    return (
      <>
        {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
          <div
            className={styles['table-body__row-skeleton-loader']}
            // Placeholders carry no information, so they are hidden from
            // assistive technology — the table itself is marked `aria-busy`.
            role="row"
            aria-hidden="true"
            // Stretched past the viewport so the skeleton spans a table wide
            // enough to scroll horizontally.
            style={{ width: `${props.columnCount * 100}%` }}
            key={`skeleton-loader-row-${index}`}
          >
            <SkeletonBase />
          </div>
        ))}
      </>
    );
  }

  return (
    // Announced as a single row spanning the table, which is how a table says
    // "nothing to show" without breaking its own structure.
    <div className={styles['table-body__empty-state-wrapper']} role="row">
      <div
        className={styles['table-body__empty-state-wrapper__state']}
        role="cell"
        aria-live="polite"
      >
        {status === 'empty' ? (
          <PageStatus
            status="default"
            title={props.emptyState?.text || ''}
            description={props.emptyState?.description || ''}
            icon={DefaultSpreadsheetSolidFile('primary')}
            keepIconDefaultColors
          />
        ) : (
          <PageStatus
            status="error"
            title={ERROR_STATE_LOCALES[lang].text}
            description={ERROR_STATE_LOCALES[lang].hintText}
            icon={CircleExclamation}
          />
        )}
      </div>
    </div>
  );
};
